import { useQuery } from '@apollo/client'
import { CheckCircle, Edit, WorkspacePremium } from '@mui/icons-material'
import {
    Box,
    Button,
    Grid,
    IconButton,
    Link as MuiLink,
    Stack,
    type SxProps,
    type Theme,
    Typography,
} from '@mui/material'
import { type FC, Fragment, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { CenteredBox } from 'shared/components/CenteredBox'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import {
    formatDateWithFallback,
    formatDuration,
    getPxAsRem,
} from 'shared/utils'
import { CandidateExperienceDrawer } from './CandidateExperienceDrawer'
import {
    GetCandidateWorkExperience,
    type CandidateWorkExperience,
} from './CandidateExperiencePanel'
import { parse, isValid } from 'date-fns'

const getExperienceSortTimestamp = (
    experience: CandidateWorkExperience[number]
): number => {
    if (experience.isCurrent) {
        return Number.MAX_SAFE_INTEGER
    }

    if (experience.endDate) {
        const parsedDate = parse(experience.endDate, 'MM-yyyy', new Date())
        if (isValid(parsedDate)) {
            return parsedDate.getTime()
        }
    }

    return Number.MIN_SAFE_INTEGER
}

const ExperienceCard: FC<{
    experience: CandidateWorkExperience[number]
    onEdit: (experience: CandidateWorkExperience[number]) => void
}> = ({ experience, onEdit }) => {
    const startDate = formatDateWithFallback(experience.startDate, 'Unknown')
    const endDate = formatDateWithFallback(
        experience.endDate,
        experience.isCurrent ? 'Present' : 'Unknown'
    )
    const duration = formatDuration(
        experience.startDate,
        experience.endDate,
        experience.isCurrent
    )
    const titleText = experience.title?.value || '-'
    const dateText = `${startDate} - ${endDate}${duration ? ` (${duration})` : ''}`
    const locationText = experience.companyOffice?.officeLocation?.value || '-'
    const industriesText =
        (experience.industries ?? [])
            .map(({ industry }) => industry.value)
            .join(', ') || '-'
    const sectorsText =
        (experience.sectors ?? [])
            .map(({ sector }) => sector.value)
            .join(', ') || '-'

    return (
        <Box
            data-testid="experience-card"
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: getPxAsRem(12),
            }}
        >
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            noWrap
                            title={`${titleText} @ ${experience.company?.value ?? ''}`}
                        >
                            <Box
                                component="span"
                                data-testid="experience-card-title"
                            >
                                {titleText}
                            </Box>
                            {' @ '}
                            <MuiLink
                                data-testid="experience-card-company"
                                component={Link}
                                to={`/companies/${experience.company?.id}`}
                                sx={(theme) => ({
                                    textDecoration: 'none',
                                    color: theme.palette.primary.main,
                                })}
                            >
                                {experience.company?.value}
                            </MuiLink>
                            {experience.isCurrent && (
                                <CheckCircle
                                    titleAccess="Current position"
                                    color="success"
                                    sx={{
                                        fontSize: getPxAsRem(14),
                                        verticalAlign: 'text-bottom',
                                        ml: getPxAsRem(4),
                                    }}
                                />
                            )}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => onEdit(experience)}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Stack>
                </Grid>

                <Grid item xs={6}>
                    <Typography variant="caption" color="text.disabled">
                        Date
                    </Typography>
                    <Typography
                        data-testid="experience-card-date"
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={dateText}
                    >
                        {dateText}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.disabled">
                        Location
                    </Typography>
                    <Typography
                        data-testid="experience-card-location"
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={locationText}
                    >
                        {locationText}
                    </Typography>
                </Grid>

                {((experience.industries ?? []).length > 0 ||
                    (experience.sectors ?? []).length > 0) && (
                    <>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.disabled">
                                Industries
                            </Typography>
                            <Typography
                                data-testid="experience-card-industries"
                                variant="body2"
                                noWrap
                                title={industriesText}
                            >
                                {(experience.industries ?? []).length > 0
                                    ? (experience.industries ?? []).map(
                                          (industry, index) => (
                                              <Fragment
                                                  key={industry.industry.id}
                                              >
                                                  {industry.topTier ? (
                                                      <Box
                                                          display="inline-flex"
                                                          alignItems="baseline"
                                                          color="primary.main"
                                                          gap={getPxAsRem(4)}
                                                      >
                                                          <WorkspacePremium
                                                              sx={{
                                                                  width: getPxAsRem(
                                                                      12
                                                                  ),
                                                                  height: getPxAsRem(
                                                                      12
                                                                  ),
                                                              }}
                                                          />
                                                          {
                                                              industry.industry
                                                                  .value
                                                          }
                                                      </Box>
                                                  ) : (
                                                      industry.industry.value
                                                  )}
                                                  {index <
                                                      (experience.industries
                                                          ?.length ?? 0) -
                                                          1 && ', '}
                                              </Fragment>
                                          )
                                      )
                                    : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.disabled">
                                Sectors
                            </Typography>
                            <Typography
                                data-testid="experience-card-sectors"
                                variant="body2"
                                noWrap
                                color="text.secondary"
                                title={sectorsText}
                            >
                                {(experience.sectors ?? []).length > 0
                                    ? (experience.sectors ?? [])
                                          .map(({ sector }) => sector.value)
                                          .join(', ')
                                    : '-'}
                            </Typography>
                        </Grid>
                    </>
                )}

                {experience.experienceDescription && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.disabled">
                            Sector Description
                        </Typography>
                        <Typography
                            data-testid="experience-card-sector-description"
                            variant="body2"
                            color="text.secondary"
                            whiteSpace="pre-line"
                        >
                            {experience.experienceDescription}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}

export const CandidateExperienceCards: FC<{ sx?: SxProps<Theme> }> = ({
    sx,
}) => {
    const [open, setOpen] = useState(false)
    const [currentWorkExperience, setCurrentWorkExperience] = useState<
        CandidateWorkExperience[number] | null
    >(null)
    const { candidateId } = useParams()

    const { data, loading: isLoading } = useQuery(GetCandidateWorkExperience, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
    })

    const workExperience = [...(data?.candidate?.workExperience ?? [])].sort(
        (a, b) => getExperienceSortTimestamp(b) - getExperienceSortTimestamp(a)
    )

    const handleClose = () => {
        setOpen(false)
        setCurrentWorkExperience(null)
    }

    const handleEdit = (experience: CandidateWorkExperience[number]) => {
        setOpen(true)
        setCurrentWorkExperience(experience)
    }

    return (
        <>
            <CandidateExperienceDrawer
                open={open}
                onClose={handleClose}
                handleCancel={handleClose}
                workExperience={currentWorkExperience}
            />

            <ProfilePanel
                sx={{ ...sx, overflow: 'hidden' } as any}
                panelTitle="Experience"
                actionElements={[
                    <Button
                        key="add-experience"
                        variant="outlined"
                        onClick={() => setOpen(true)}
                    >
                        Add Experience
                    </Button>,
                ]}
            >
                {isLoading ? (
                    <LoadingPanel />
                ) : workExperience.length === 0 ? (
                    <CenteredBox>
                        <Typography>No work experience found</Typography>
                    </CenteredBox>
                ) : (
                    <Stack
                        gap={getPxAsRem(8)}
                        sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}
                    >
                        {workExperience.map((experience) => (
                            <ExperienceCard
                                key={experience.id}
                                experience={experience}
                                onEdit={handleEdit}
                            />
                        ))}
                    </Stack>
                )}
            </ProfilePanel>
        </>
    )
}
